import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Profile } from '../../lib/supabase';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export default function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // Si hay error de refresh token, limpiar y salir
      if (sessionError) {
        console.error('Error obteniendo sesión:', sessionError);
        if (sessionError.message?.includes('Refresh Token')) {
          await supabase.auth.signOut();
        }
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      if (!session) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single<Profile>();

      // Si hay error obteniendo el perfil, cerrar sesión
      if (profileError) {
        console.error('Error obteniendo perfil:', profileError);
        await supabase.auth.signOut();
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // FIX: Comparación case-insensitive y trim
      const role = (profile?.role || '').toLowerCase().trim();
      if (role === 'admin') {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error: any) {
      console.error('Error verificando acceso:', error);
      
      // Si es error de autenticación, limpiar sesión
      if (error?.message?.includes('Refresh Token') || error?.message?.includes('Invalid Refresh Token')) {
        await supabase.auth.signOut();
      }
      
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-600 text-center mb-6">
          Tu enlace de acceso ha sido desactivado por seguridad. Para ver tus fotos y videos, por favor solicita acceso nuevamente contactándonos. Estamos aquí para ayudarte.
        </p>
        <Navigate to="/admin/login" replace />
      </div>
    );
  }

  return <>{children}</>;
}
